import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
} from "react"
import * as z from "zod"
import GuestCheckoutSummary from "./GuestCheckoutSummary"
import Loader from "../ui/loader"
import Recaptcha from "../auth/register-form/Recaptcha"
import secureLocalStorage from "react-secure-storage"
import SelfEnrollBlockModal from "./SelfEnrollBlockModal"
import useLoading from "../../hooks/useLoading"
import { Button } from "../ui/button"
import { ContactRequests } from "../../api/app.service"
import { CommonFunctionCtx } from "../../context/commonFunctionContext"
import { Dialog, DialogContent, DialogHeader } from "../ui/dialog"
import { EmailLogin } from "../../api/auth.service"
import { getUserStats } from "../../utils/getUserStats"
import { Input } from "../ui/input"
import { InputOTPGroup, InputOTPSlot, InputOTP } from "../ui/input-otp"
import { isLocalStorageAvailable } from "../../utils/isLocalStorageAvailable"
import { Label } from "../ui/label"
import { LoginFormContext } from "../auth/login-form/context"
import { SvgWarning, SvgClose } from "../icons"
import { UserCtx } from "../../context/userContext"
import { UserExists } from "../../api/auth.service"
import { useToast } from "../../hooks/useToast"
import { useParams } from "react-router-dom"
import { VerifyConfirmationCode } from "../../api/auth.service"

interface Props {
  coachPublicProfileData: any
  service: any
  guestEmail: any
  sentVerification: any
  cardFieldErrors: {
    cardName: boolean
    cardNumber: boolean
    cardExpiry: boolean
    cardCvv: boolean
  }
  setCardFieldErrors: React.Dispatch<
    React.SetStateAction<{
      cardName: boolean
      cardNumber: boolean
      cardExpiry: boolean
      cardCvv: boolean
    }>
  >
}

const ServiceGuestLogin: React.FC<Props> = ({
  coachPublicProfileData,
  service,
  guestEmail,
  sentVerification,
  cardFieldErrors,
  setCardFieldErrors,
}) => {
  const { toast } = useToast()
  const { user } = useContext(UserCtx)
  const { getUser } = useContext(CommonFunctionCtx)
  const { endpoint_slug: endpointSlug } = useParams<{
    endpoint_slug: string
  }>()

  const {
    email,
    setEmail,
    password,
    setPassword,
    getSetupInfo,
    updateTimezone,
    onSuccess,
  } = useContext(LoginFormContext)
  const { startLoading, stopLoading } = useLoading()

  const [loadingAvailablePeriods, setLoadingAvailablePeriods] =
    useState<boolean>(false)
  const [discountedOrderTotal, setDiscountedOrderTotal] = useState(0)
  const [isOwner, setIsOwner] = useState<boolean>()
  const [activeContact, setActiveContact] = useState<any>(null)
  const [isNotRobot, setIsNotRobot] = useState<boolean>(false)
  const [connectionCompleted, setConnectionCompleted] = useState<boolean>(false)
  const [leftHeight, setLeftHeight] = useState<number | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [showresetModal, setShowresetModal] = useState(false)
  type AuthMode = "password" | "otp_verify"
  const [sendingOtp, setSendingOtp] = useState(false)
  const [authMode, setAuthMode] = useState<AuthMode>("password")
  const [otpCode, setOtpCode] = useState("")
  const [otpSession, setOtpSession] = useState<any>(null)
  const [profileType, setProfileType] = useState()
  const [showSelfEnrollBlockedModal, setShowSelfEnrollBlockedModal] =
    useState(false)
  const [errors, setErrors] = useState({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    confUserPassword: "",
  })

  useLayoutEffect(() => {
    if (leftContainerRef.current) {
      setLeftHeight(leftContainerRef.current.offsetHeight)
    }
  }, [loadingAvailablePeriods])

  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  const leftContainerRef = useRef<HTMLDivElement | null>(null)

  const [emailError, setEmailError] = useState<string | undefined>()
  const [authError, setAuthError] = useState<string | undefined>()
  const validateEmail = (email: string) => z.string().email().safeParse(email)
  const validatePassword = (password: string) =>
    z
      .string()
      .min(8, "Invalid password")
      .regex(/^(?=.*[0-9])(?=.*\W).{8,}$/, {
        message: "Incorrect Password. Please try again",
      })
      .safeParse(password)

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setLoading(true)
    setEmailError(undefined)
    setAuthError(undefined)

    try {
      const emailCheck = validateEmail(email)
      if (!emailCheck.success) {
        setEmailError(emailCheck.error.issues[0].message)
        return
      }

      const emailExists = await doesEmailExist(email)
      if (!emailExists) {
        setEmailError("Email not found")
        return
      }

      secureLocalStorage.setItem(
        "coachDetails",
        JSON.stringify(coachPublicProfileData)
      )

      const success = await login()

      if (!success) {
        if (authMode === "password") {
          toast.error("Incorrect email or password.")
        } else {
          toast.error("The code you entered is incorrect. Please try again.")
        }
        return
      }
    } catch (err: any) {
      console.error(err)
      setAuthError("An unexpected error occurred")
    } finally {
      setLoading(false)
    }
  }

  const handleSendOtp = async () => {
    setEmailError(undefined)

    if (!email) {
      setEmailError("Please enter your email.")
      return
    }

    const exists = await doesEmailExist(email)
    if (!exists) {
      setEmailError("Email not found.")
      return
    }

    try {
      setSendingOtp(true)

      const session = await EmailLogin.loginOTP({ email })
      setOtpSession(session)
      setAuthMode("otp_verify")

      toast.success("We sent a one-time code to your email.")
    } catch (err) {
      console.error(err)
      toast.error("Failed to send one-time password.")
    } finally {
      setSendingOtp(false)
    }
  }

  const login = async () => {
    try {
      let data: any | null = null
      if (authMode === "password") {
        const passwordCheck = validatePassword(password)
        if (!passwordCheck.success) {
          setAuthError(passwordCheck.error.issues[0].message)
          return false
        }

        data = await EmailLogin.loginEmail({
          email,
          password,
          stats: getUserStats(),
        })
      }

      if (authMode === "otp_verify") {
        if (otpCode.length !== 6) {
          toast.error("Enter the 6-digit code")
          return false
        }

        if (!otpSession) {
          toast.error("OTP session expired. Please resend the code.")
          return false
        }

        data = await VerifyConfirmationCode.verifyConfirmationCode({
          confirmation_code: otpCode,
          session: otpSession.session,
          user_id: otpSession.user_id,
          stats: getUserStats(),
        })
      }

      if (!data) {
        throw new Error("No data returned from authentication")
      }

      if (isLocalStorageAvailable()) {
        secureLocalStorage.setItem("user", JSON.stringify(data))
      } else {
        toast.error("Local storage is not available")
      }

      await Promise.all([
        getSetupInfo(data.user_id),
        updateTimezone(data.user_id, data.access_token),
      ])

      const authedUser = await getUser()
      onSuccess(authedUser)

      const rawCoachDetails = secureLocalStorage.getItem("coachDetails")
      const coachDetails =
        typeof rawCoachDetails === "string" ? JSON.parse(rawCoachDetails) : null

      const isSameUserAsCoach =
        coachDetails &&
        (authedUser.user_id === coachDetails.coach_user_id ||
          authedUser.activeProfileId === coachDetails.coach_profile_id)

      if (isSameUserAsCoach) {
        secureLocalStorage.removeItem("coachDetails")
        setShowSelfEnrollBlockedModal(true)
        return true
      }

      if (coachDetails) {
        let foundContact: any = null

        try {
          const contactData = await ContactRequests.getContactList({
            profile_id: authedUser.activeProfileId,
            profile_type: authedUser.activeProfile,
          })

          foundContact = contactData.contact_list?.find(
            (contact: any) => contact.endpoint_slug === endpointSlug
          )
        } catch (ex) {
          console.error("Error fetching contact list:", ex)
        }

        if (!foundContact) {
          try {
            await ContactRequests.createContactConnection({
              sender_profile_id: coachDetails.coach_profile_id,
              receiver_profile_id: authedUser.activeProfileId,
              user_environment: process.env.REACT_APP_USER_ENVIRONMENT || "",
              status: authedUser.isCoach ? "c2c" : "connected",
            })
          } catch (ex) {
            console.error("Failed to create contact connection:", ex)
          }
        }

        setActiveContact(foundContact)
        setConnectionCompleted(true)
        secureLocalStorage.removeItem("coachDetails")
      }

      return true
    } catch (error: any) {
      console.error(error)
      setAuthError(error.response?.data?.message || "Authentication failed")
      return false
    }
  }

  const canSubmit =
    !!email?.trim() &&
    !!isNotRobot &&
    (authMode === "password" ? !!password?.trim() : otpCode.length === 6)

  const forgotPassword = async () => {
    startLoading("Sending instructions")
    const pathname = `/${profileType}/${coachPublicProfileData.endpoint_slug}/services/${service.service_id}`

    setEmailError(undefined)

    if (!email) {
      setEmailError("Please enter your email.")
      stopLoading()
      return
    }

    try {
      const emailExists = await doesEmailExist(email)
      if (!emailExists) {
        setEmailError("Email not found.")
        return
      }

      const resetPassword = await EmailLogin.resetPasswordSendLink({
        email,
        pathname,
      })

      if (resetPassword.message === "complete") {
        setShowresetModal(true)
      }
    } catch (error) {
      console.error(error)
      toast.error("Something went wrong. Please try again.")
    } finally {
      stopLoading()
    }
  }

  const doesEmailExist = async (email: string): Promise<boolean> => {
    try {
      const data = await UserExists.verifyUserExists({
        attributes_to_verify: { email },
      })
      setProfileType(data.profile_type)
      return data.user_found
    } catch (error) {
      console.error(error)
      return false
    }
  }

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmail(value)

    const emailSchema = z.string().email().safeParse(value)
    if (!emailSchema.success) {
      return
    }
  }

  useEffect(() => {
    if (String(coachPublicProfileData?.user_id) === user?.user_id) {
      setIsOwner(true)
    } else {
      setIsOwner(false)
    }
  }, [coachPublicProfileData, user])

  const initialEmailSet = useRef(false)

  useEffect(() => {
    if (!initialEmailSet.current && guestEmail) {
      setEmail(guestEmail)
      initialEmailSet.current = true
    }
  }, [guestEmail, setEmail])

  useEffect(() => {
    if (!email) return

    if (debounceTimer.current) clearTimeout(debounceTimer.current)

    debounceTimer.current = setTimeout(async () => {
      try {
        const result = await doesEmailExist(email)
        sentVerification(result)
      } catch (err) {
        console.error(err)
      }
    }, 500)

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [email])

  return (
    <div className="flex flex-col items-center w-full mb-4">
      {" "}
      <div className="gap-4 md:gap-20 mb-4 w-full p-0">
        <div className="flex flex-col gap-[12px] px-[20px] md:col-span-2 justify-center items-center w-full">
          <div className="w-full font-bold">Enter your information below:</div>
          <div className="flex flex-col w-[100%]">
            <Input
              label="Email"
              id="email"
              placeholder="Email Address"
              value={email}
              onChange={handleEmailChange}
              disabled={loading}
              className="border-grayCloud focus:border-blurple hover:bg-grayFlash hover:border-grayFlash h-14 cursor-text rounded-md pl-4 w-full border-2 focus:outline-none"
              style={{
                backgroundColor: errors.email ? "rgba(255, 0, 0, 0.05)" : "",
              }}
            />
          </div>
          <div className="flex flex-col w-[100%]">
            {authMode === "password" && (
              <>
                <Input
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  className="border-grayCloud focus:border-blurple hover:bg-grayFlash hover:border-grayFlash h-14 cursor-text rounded-md pl-4 w-full border-2 focus:outline-none"
                />

                <div className="flex flex-col gap-1 mt-2 text-sm items-center">
                  {sendingOtp ? (
                    <div className="mt-2.5">
                      <Loader />
                    </div>
                  ) : (
                    <Button
                      variant="link"
                      onClick={handleSendOtp}
                      disabled={sendingOtp}
                    >
                      Send one-time password
                    </Button>
                  )}

                  <Button
                    variant="link"
                    onClick={forgotPassword}
                    disabled={sendingOtp}
                  >
                    Forgot Password?
                  </Button>
                </div>
              </>
            )}

            {authMode === "otp_verify" && (
              <div className="flex flex-col items-center w-full mt-2">
                <Label className="text-center mb-3">
                  Enter the 6-digit code sent to your email
                </Label>

                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otpCode}
                    onChange={setOtpCode}
                    disabled={loading}
                    autoFocus
                  >
                    <InputOTPGroup className="justify-center">
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot key={i} index={i} />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <div className="flex flex-col gap-1 mt-3 text-sm items-center">
                  <Button variant="link" onClick={handleSendOtp}>
                    Resend One-time Password
                  </Button>
                  <Button
                    variant="link"
                    onClick={() => setAuthMode("password")}
                  >
                    Back to password
                  </Button>
                </div>
              </div>
            )}
            {errors.email && (
              <div className="flex items-center text-red-500 text-sm mt-1">
                <SvgWarning className="w-[18px] h-[18px] mr-1" />
                {errors.email}
              </div>
            )}
          </div>
          <div className="w-full">
            <GuestCheckoutSummary
              coachPublicProfileData={coachPublicProfileData}
              service={service}
              connectionCompleted={connectionCompleted}
              setLoading={setLoading}
              cardFieldErrors={cardFieldErrors}
              setCardFieldErrors={setCardFieldErrors}
              discountedOrderTotal={discountedOrderTotal}
              setDiscountedOrderTotal={setDiscountedOrderTotal}
            />
          </div>

          <div className="mt-2 w-full">
            <div className="mb-4">
              <Recaptcha setIsNotRobot={setIsNotRobot} />
            </div>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || loading}
              className="w-full text-md ml-2"
            >
              {loading ? <Loader /> : "Continue"}
            </Button>
          </div>
        </div>
      </div>
      <Dialog open={showresetModal} onOpenChange={setShowresetModal}>
        <DialogContent className="">
          <DialogHeader className="text-center items-center">
            <button
              onClick={() => setShowresetModal(false)}
              className="absolute top-6 right-6 text-gray-500 hover:text-gray-800"
              aria-label="Close"
            >
              <SvgClose />
            </button>
            <p className="-mt-4">
              Instructions to reset your password have been sent to your email.
              Please check your inbox and follow the steps provided.
              <br />
              <br />
              Have questions? Contact zoee support at
              <br />
              <a
                className="underline font-bold"
                href="mailto:businessblueprint@integrativenutrition.com"
                target="_blank"
                rel="noreferrer"
              >
                support@zoee.com
              </a>
            </p>
          </DialogHeader>
        </DialogContent>
      </Dialog>
      {showSelfEnrollBlockedModal && <SelfEnrollBlockModal coachPublicProfileData={coachPublicProfileData}/>}
    </div>
  )
}

export default ServiceGuestLogin
